import type { IconName } from '.';

/**
 * Represents an icon with its transformation properties.
 * https://docs.fontawesome.com/web/style/power-transform
 */
export class IconWithTransform {
    public up?: number;
    public down?: number;
    public right?: number;
    public left?: number;
    public grow?: number;
    public shrink?: number;
    public color?: string;

    constructor(init?: Partial<IconWithTransform>) {
        this.down = init?.down;
        this.up = init?.up;
        this.right = init?.right;
        this.left = init?.left;
        this.shrink = init?.shrink;
        this.grow = init?.grow;
        this.color = init?.color;
    }

    public toFontAwesomeStackString(): string {
        return (
            (this.up ? `up-${this.up} ` : '') +
            (this.down ? `down-${this.down} ` : '') +
            (this.right ? `right-${this.right} ` : '') +
            (this.left ? `left-${this.left} ` : '') +
            (this.shrink ? `shrink-${this.shrink} ` : '') +
            (this.grow ? `grow-${this.grow} ` : '')
        );
    }
}

/**
 * Represents an icon with its position properties.
 */
export type IconWithPosition = { iconName: IconName; transform?: IconWithTransform };

/**
 * Represents an icon with its stacking properties.
 */
export interface IStackedIcon {
    iconName: IconName;
    stackedIcons?: IconWithPosition[];
}

/**
 * Represents an icon with its stacking properties.
 * https://docs.fontawesome.com/web/style/stack
 */
export class StackedIcon implements IStackedIcon {
    iconName: IconName;
    rotate?: number;
    stackedIcons?: IconWithPosition[];

    constructor(iconName: IconName, stackedIcons?: IconWithPosition[], rotate?: number) {
        this.iconName = iconName;
        this.stackedIcons = stackedIcons;
        this.rotate = rotate;
    }

    static FromStackedIcon(icon: IStackedIcon): StackedIcon {
        const stackedIcon = new StackedIcon(icon.iconName, icon.stackedIcons);
        stackedIcon.stackedIcons?.forEach((iconWithPosition) => {
            iconWithPosition.transform = new IconWithTransform(iconWithPosition.transform);
        });
        return stackedIcon;
    }
}
